import React, { useContext, useEffect, useRef, useState } from "react";
import { GreenMachineContext } from "../GreenMachineContext";
import { commitMutation, graphql, QueryRenderer } from "react-relay";
import environment from "../../../../../RelayEnvironment";
import PlayerTable from "../PlayerTable";
import relayEnvironment from "../../../../../RelayEnvironment";
import { COMMIT_PLAYER_CHANGES } from "./mutations/CommitPlayerSelectionChanges";
import "./SelectPlayers.css";
import PlayerSelectAnalysis from "./PlayerSelectAnalysis";

const GET_PLAYERS = graphql`
  query SelectPlayersQuery($slateID: ID!, $gmID: ID!) {
    playersForSlate(slateId: $slateID) {
      id
      player {
        id
        name
        shortenedName
        team {
          abbreviation
          name
        }
      }
      position
      fppg
      salary
      rosterSlots {
        edges {
          node {
            name
          }
        }
      }
      slateCompetition {
        prettyStartTime
      }
      gupPercProj
      projPoints
      suffix
    }

    greenMachineSession(id: $gmID) {
      id
      user {
        username
      }

      slate {
        prettyStartTime
      }

      selectedPlayerIds
      numLineups
    }
  }
`;

export default function SelectPlayers() {
  const {
    greenMachineSessionID,
    greenMachineSessionPositions,
    selectedSlateID,
    currentStage,
    requestNavigation,
    registerStepNavGuard,
    clearStepNavGuard,
    isNavigating,
    setErrorMessage,
    minSalary,
    maxSalary,
    playersPerLineup,
    STAGE_NUMBERS,
  } = useContext(GreenMachineContext);

  const STAGE_NUMBER = STAGE_NUMBERS.selectPlayers;

  const [unsavedSelectionChanges, setUnsavedSelectionChanges] = useState({});
  const [sessionSelected, setSessionSelected] = useState({});
  const [totalSalary, setTotalSalary] = useState(undefined);
  const [totalPlayers, setTotalPlayers] = useState(undefined);

  const avgSalary =
    totalPlayers > 0 ? Math.round(totalSalary / totalPlayers) : 0;

  const targetMinSalaryAverage = Math.round(minSalary / playersPerLineup);
  const targetMaxSalaryAverage = Math.round(maxSalary / playersPerLineup);

  const salaryCheck =
    avgSalary >= targetMinSalaryAverage && avgSalary <= targetMaxSalaryAverage;

  function updateUnsavedSelections(player, state, salary) {
    setUnsavedSelectionChanges({
      ...unsavedSelectionChanges,
      [player.id]: state,
    });
    setSessionSelected({ ...sessionSelected, [player.id]: state });

    if (state) {
      setTotalPlayers(totalPlayers + 1);
      setTotalSalary(totalSalary + salary);
    } else {
      setTotalPlayers(totalPlayers - 1);
      setTotalSalary(totalSalary - salary);
    }
  }

  // Persist player selections on the way out (Continue or a chevron jump).
  const saveRef = useRef();
  saveRef.current = () =>
    new Promise((resolve) => {
      setErrorMessage(undefined);

      let addPlayers = [];
      let removePlayers = [];

      for (const [key, value] of Object.entries(unsavedSelectionChanges)) {
        if (value) {
          addPlayers.push(key);
        } else {
          removePlayers.push(key);
        }
      }

      if (addPlayers.length === 0 && removePlayers.length === 0) {
        resolve(true);
        return;
      }

      commitMutation(relayEnvironment, {
        mutation: COMMIT_PLAYER_CHANGES,
        variables: {
          input: {
            greenMachineSessionId: greenMachineSessionID,
            addPlayers: addPlayers,
            removePlayers: removePlayers,
          },
        },
        onCompleted: (content, errors) => {
          if (!errors) {
            setUnsavedSelectionChanges({});
            resolve(true);
          } else {
            setErrorMessage(errors[0].message);
            resolve(false);
          }
        },
        onError: (error) => {
          console.log(error);
          setErrorMessage("Something went wrong saving your changes.");
          resolve(false);
        },
      });
    });

  useEffect(() => {
    if (currentStage !== STAGE_NUMBER) {
      return undefined;
    }
    registerStepNavGuard({ saveAndValidate: () => saveRef.current() });
    return () => clearStepNavGuard();
  }, [currentStage, STAGE_NUMBER, registerStepNavGuard, clearStepNavGuard]);

  if (currentStage < STAGE_NUMBER) {
    return null;
  }

  // PlayerTable triggers an explicit save through this thin wrapper.
  function commitChanges() {
    saveRef.current();
  }

  function selectAll(e, players) {
    e.preventDefault();

    let selected = {};
    let totSalary = 0;
    players.forEach((player) => {
      selected[player.id] = true;
      totSalary += player.salary;
    });

    setTotalSalary(totSalary);
    setTotalPlayers(players.length);

    setSessionSelected(selected);
    setUnsavedSelectionChanges(selected);
  }

  function deselectAll(e, players) {
    e.preventDefault();
    let deselected = {};
    players.forEach((player) => (deselected[player.id] = false));
    setSessionSelected(deselected);
    setUnsavedSelectionChanges(deselected);

    setTotalSalary(0);
    setTotalPlayers(0);
  }

  if (currentStage > STAGE_NUMBER) {
    return (
      // <div>
      //   {props.greenMachineSession.selectedPlayerIds.length +
      //     Object.values(sessionSelected).filter(Boolean).length}{" "}
      //   players selected.
      // </div>
      null
    );
  }

  return (
    <QueryRenderer
      environment={environment}
      query={GET_PLAYERS}
      variables={{
        slateID: selectedSlateID,
        gmID: greenMachineSessionID,
      }}
      render={({ error, props }) => {
        if (error) {
          return <div>Something went wrong.</div>;
        }

        if (!props) {
          return (
            <div className="gm-player-select-container">Loading players...</div>
          );
        }

        if (totalSalary === undefined) {
          const alreadySelected = props.playersForSlate.filter((player) =>
            props.greenMachineSession.selectedPlayerIds.includes(player.id)
          );

          let totSalary = alreadySelected.reduce((a, b) => +a + +b.salary, 0);

          setTotalSalary(totSalary);
          setTotalPlayers(alreadySelected.length);
        }

        return (
          <div className="gm-player-select-container">
            <div className="gm-step-actions">
              <button
                type="button"
                className="gm-btn-secondary"
                onClick={(e) => selectAll(e, props.playersForSlate)}
              >
                Select All
              </button>

              <button
                type="button"
                className="gm-btn-secondary"
                onClick={(e) => deselectAll(e, props.playersForSlate)}
              >
                Deselect All
              </button>

              <button
                type="button"
                className="gm-btn-primary"
                disabled={isNavigating}
                onClick={() => requestNavigation(STAGE_NUMBERS.configureRules)}
              >
                Continue
                <svg className="gm-cta-arrow" viewBox="0 0 24 24">
                  <path d="M8 5l8 7-8 7V5z" />
                </svg>
              </button>
            </div>

            <p>
              <strong>NUMBER OF PLAYERS SELECTED:</strong> {totalPlayers}
            </p>
            <p className={salaryCheck ? "gm-good-text" : "gm-bad-text"}>
              <strong>AVERAGE SALARY:</strong> {avgSalary}
              {!salaryCheck ? (
                <p>
                  Should be between <strong>{targetMinSalaryAverage}</strong>{" "}
                  and <strong>{targetMaxSalaryAverage}</strong> based on your
                  salary restrictions.
                </p>
              ) : null}
            </p>

            <PlayerSelectAnalysis
              positions={greenMachineSessionPositions}
              players={props.playersForSlate.filter((player) => {
                if (player.id in sessionSelected) {
                  return sessionSelected[player.id];
                }

                return props.greenMachineSession.selectedPlayerIds.includes(
                  player.id
                );
              })}
            />

            <PlayerTable
              players={props.playersForSlate}
              commitChanges={commitChanges}
              updateChanges={updateUnsavedSelections}
              selectedPlayers={props.greenMachineSession.selectedPlayerIds}
              sessionSelected={sessionSelected}
            />
          </div>
        );
      }}
    />
  );
}
